import { createContext } from 'react'
import type { Brand } from '../../../types/brand'
import type { CategoryItem } from '../../../gateways/breadstack/categoryApis'
import type { Product, ProductListParams, ProductListResponse, StoreType, ProductSortBy } from '../../../types/product'
import type {
  ProductFilters,
  ActiveTab,
  ActiveStatCard,
  ViewMode,
  RemovableFilterKey,
  PageScoreFilterValue,
} from '../hooks/useProductFilters'
import type { ActiveFilterChip } from '../hooks/useActiveFilterChips'
import type { FilterOption } from '../constants'

export interface ProductContextValue {
  storeId: string
  storeType: StoreType

  listData: ProductListResponse | undefined
  products: Product[]
  isLoading: boolean
  isFetching: boolean

  brands: Brand[]
  brands_map: Record<string, Brand>

  categories: CategoryItem[]
  categories_map: Record<string, CategoryItem>

  filters: ProductFilters
  hasActiveFilters: boolean
  patch: (partial: Partial<ProductFilters>) => void
  setLimit: (limit: number) => void
  resetFilters: () => void
  // Multi-select Category / Brand filters (ids; backend matches ANY).
  setCategoryIds: (ids: string[]) => void
  setBrandIds: (ids: string[]) => void
  // Removes a single "Selected filters" chip value.
  removeFilterValue: (key: RemovableFilterKey, value?: string) => void
  // Clears search + every filter + stat card; keeps tab, view, sort, page size.
  clearAllFilters: () => void
  // Active filters as removable chips with resolved names (empty until the
  // brand/category option lists have loaded).
  filterChips: ActiveFilterChip[]
  // Location filter options (from the store's warehouses).
  locationOptions: FilterOption[]
  setTab: (tab: ActiveTab) => void
  setStatCard: (card: ActiveStatCard) => void
  setPageScoreFilter: (value: PageScoreFilterValue) => void
  setView: (view: ViewMode) => void
  toggleSort: (col: string) => void
  cycleSortBy: (col: ProductSortBy) => void

  // Live (undebounced) search draft + setter for the toolbar's search input —
  // `filters.search` only updates once the 300ms debounce settles.
  searchInput: string
  setSearchInput: (value: string) => void

  // Effective server params for the current filters — reused by the CSV export
  // (page/limit are stripped gateway-side).
  queryParams: ProductListParams

  // Builds the edit-detail href for a product (e.g. for a real `<Link>` on the
  // product name). The lib stays store-agnostic — the host app supplies the
  // route-construction logic. Optional so the lib remains usable without it.
  getEditHref?: (productId: string) => string

  // Export-CSV plumbing. The host wrapper supplies the user's UTC-offset and the
  // dialog/toast hooks; the library stays headless w.r.t. main-app primitives.
  timeZone?: number
  onExportQueued?: (exportId: string) => void
  onExportReady?: () => void
  onExportError?: (error: unknown) => void
}

export const ProductContext = createContext<ProductContextValue | undefined>(undefined)
